import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentationUpload = ({ documentsData, onUploadDocument, onDeleteDocument }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'certificate':
        return 'Award';
      case 'training':
        return 'GraduationCap';
      case 'compliance':
        return 'Shield';
      case 'photo':
        return 'Camera';
      default:
        return 'FileText';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'var(--color-success)';
      case 'pending':
        return 'var(--color-warning)';
      case 'rejected':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = (files) => {
    files?.forEach((file) => {
      const fileId = Date.now() + Math.random();
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const currentProgress = prev?.[fileId] || 0;
          if (currentProgress >= 100) {
            clearInterval(interval);
            onUploadDocument(file, fileId);
            return { ...prev, [fileId]: 100 };
          }
          return { ...prev, [fileId]: currentProgress + 10 };
        });
      }, 200);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Documentación
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Certificados y evidencias de cumplimiento
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Upload" size={20} color="var(--color-primary)" />
        </div>
      </div>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragOver 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <h3 className="font-medium text-foreground mb-2">
          Arrastra archivos aquí o haz clic para seleccionar
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Formatos soportados: PDF, JPG, PNG, DOC (máx. 10MB)
        </p>
        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
        />
        <Button variant="outline" asChild>
          <label htmlFor="file-upload" className="cursor-pointer">
            <Icon name="FolderOpen" size={16} className="mr-2" />
            Seleccionar Archivos
          </label>
        </Button>
      </div>
      {/* Upload Progress */}
      {Object.keys(uploadProgress)?.length > 0 && (
        <div className="mt-6 space-y-3">
          <h3 className="font-medium text-foreground">Subiendo archivos...</h3>
          {Object.entries(uploadProgress)?.map(([fileId, progress]) => (
            <div key={fileId} className="bg-muted/30 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">
                  Archivo {fileId?.slice(-4)}
                </span>
                <span className="text-sm text-muted-foreground">{progress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Document Categories */}
      <div className="mt-8 space-y-6">
        {documentsData?.categories?.map((category) => (
          <div key={category?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={getDocumentIcon(category?.type)} 
                  size={20} 
                  color="var(--color-primary)" 
                />
                <div>
                  <h3 className="font-medium text-foreground">{category?.title}</h3>
                  <p className="text-sm text-muted-foreground">{category?.description}</p>
                </div>
              </div>
              {category?.required && (
                <span className="px-2 py-1 bg-error/10 text-error text-xs rounded-full">
                  Obligatorio
                </span>
              )}
            </div>

            {/* Documents in Category */}
            <div className="space-y-3">
              {category?.documents?.map((document) => (
                <div key={document?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={getStatusIcon(document?.status)} 
                      size={16} 
                      color={getStatusColor(document?.status)} 
                    />
                    <div>
                      <p className="font-medium text-foreground text-sm">{document?.name}</p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>{formatFileSize(document?.size)}</span>
                        <span>Subido: {formatDate(document?.uploadDate)}</span>
                        {document?.expiryDate && (
                          <span>Expira: {formatDate(document?.expiryDate)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="icon">
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button variant="ghost" size="icon">
                      <Icon name="Download" size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => onDeleteDocument(document?.id)}
                    >
                      <Icon name="Trash2" size={16} color="var(--color-error)" />
                    </Button>
                  </div>
                </div>
              ))}

              {category?.documents?.length === 0 && (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No hay documentos en esta categoría
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Mexican Compliance Notice */}
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5" />
          <div>
            <h4 className="font-medium text-foreground mb-1">
              Cumplimiento Normativo Mexicano
            </h4>
            <p className="text-sm text-muted-foreground">
              Todos los documentos deben cumplir con las regulaciones mexicanas de seguridad alimentaria, 
              higiene y protección de datos. Asegúrate de que tus certificados estén vigentes y sean 
              emitidos por autoridades reconocidas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationUpload;